import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, MessageSquare } from 'lucide-react'
import { useUserDetail } from '@/hooks/useUsers'
import { useChurnAnalysis } from '@/hooks/useAnalysis'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PageLoading } from '@/components/shared/Loading'
import { UserInfo } from '@/features/users/UserInfo'
import { UserStats } from '@/features/users/UserStats'
import { ThreadList } from '@/features/users/ThreadList'
import { ChurnAnalysis } from '@/features/analysis/ChurnAnalysis'

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const { user, greenmate, stats, loading, error } = useUserDetail(userId!)
  const {
    analysis,
    analyze,
    analyzing,
  } = useChurnAnalysis(userId!)

  if (loading) return <PageLoading />

  if (error || !user) {
    return (
      <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
        <p className="text-destructive">
          {error || '사용자를 찾을 수 없습니다'}
        </p>
        <Button variant="outline" onClick={() => navigate('/users')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          사용자 목록으로 돌아가기
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          뒤로
        </Button>
        <h1 className="text-2xl font-bold">사용자 상세 정보</h1>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* User Info */}
        <Card>
          <CardContent className="pt-6">
            <UserInfo user={user} greenmate={greenmate} />
          </CardContent>
        </Card>

        {/* Churn Risk */}
        <Card>
          <CardHeader>
            <CardTitle>이탈 위험</CardTitle>
          </CardHeader>
          <CardContent>
            <ChurnAnalysis
              analysis={analysis}
              onAnalyze={() => analyze()}
              loading={analyzing}
            />
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>빠른 작업</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button asChild className="w-full">
              <Link to={`/users/${userId}/chats`}>
                <MessageSquare className="mr-2 h-4 w-4" />
                모든 채팅 보기
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Stats */}
      <UserStats stats={stats} />

      {/* Threads */}
      <Card>
        <CardHeader>
          <CardTitle>대화 스레드</CardTitle>
        </CardHeader>
        <CardContent>
          <ThreadList userId={userId!} />
        </CardContent>
      </Card>
    </div>
  )
}
